import {
  Badge,
  Banner,
  BlockStack,
  Button,
  ButtonGroup,
  Checkbox,
  DataTable,
  InlineStack,
  Layout,
  Card,
  Page,
  Select,
  TextField,
} from '../components/admin-ui';
import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../comomComponens/modal";
import {
  create_update_shop_link,
  query_shop_links,
  sync_shop_link,
} from "../api";

const syncLabels = {
  0: "Pending",
  1: "Queued",
  2: "Syncing",
  3: "Completed",
  4: "Failed",
};

export default function ShopLinks() {
  const [links, setLinks] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [status, setStatus] = useState("1");
  const [paymentPercentage, setPaymentPercentage] = useState("0");
  const [sourcePurchaseEventsEnabled, setSourcePurchaseEventsEnabled] =
    useState(false);
  const [message, setMessage] = useState(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await query_shop_links({});
      if (response.status === 200) {
        setLinks(response.data.links || []);
        setShops(response.data.shops || []);
      } else {
        setMessage({ tone: "critical", text: response.msg || "Unable to load shop links." });
      }
    } catch (error) {
      setMessage({ tone: "critical", text: "Unable to load shop links." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const shopOptions = useMemo(
    () => [
      { label: "Select a shop", value: "" },
      ...shops.map((shop) => ({
        label: `${shop.shop_name}${shop.app_name ? ` (${shop.app_name})` : ""}`,
        value: String(shop.id),
      })),
    ],
    [shops]
  );

  const unavailableShopIds = useMemo(() => {
    const ids = new Set();
    links.forEach((link) => {
      if (String(link.id) === String(editingId || "")) return;
      ids.add(String(link.source_user_id));
      ids.add(String(link.target_user_id));
    });
    return ids;
  }, [editingId, links]);

  const availableShopOptions = useMemo(
    () =>
      shopOptions.filter(
        (option) => !option.value || !unavailableShopIds.has(option.value),
      ),
    [shopOptions, unavailableShopIds],
  );

  const openCreate = () => {
    setEditingId(null);
    setSourceId("");
    setTargetId("");
    setStatus("1");
    setPaymentPercentage("0");
    setSourcePurchaseEventsEnabled(false);
    setMessage(null);
    setModalOpen(true);
  };

  const openEdit = (link) => {
    setEditingId(link.id);
    setSourceId(String(link.source_user_id));
    setTargetId(String(link.target_user_id));
    setStatus(String(link.status));
    setPaymentPercentage(String(link.payment_percentage ?? 0));
    setSourcePurchaseEventsEnabled(
      Boolean(link.source_purchase_events_enabled),
    );
    setMessage(null);
    setModalOpen(true);
  };

  const saveLink = async () => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setSaving(true);
    try {
      const response = await create_update_shop_link({
        id: editingId || "",
        source_user_id: sourceId,
        target_user_id: targetId,
        status,
        payment_percentage: Number(paymentPercentage),
        source_purchase_events_enabled: sourcePurchaseEventsEnabled ? 1 : 0,
      });
      if (response.status === 200) {
        setModalOpen(false);
        setMessage({
          tone: "success",
          text: response.data.sync_queued
            ? "Shop relationship saved and initial product sync queued."
            : "Shop relationship saved.",
        });
        await loadLinks();
      } else {
        setMessage({ tone: "critical", text: response.msg || "Unable to save shop link." });
      }
    } catch (error) {
      setMessage({
        tone: "critical",
        text:
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Unable to save shop link.",
      });
    } finally {
      setSaving(false);
    }
  };

  const queueSync = async (id) => {
    setSyncingId(id);
    try {
      const response = await sync_shop_link({ id });
      if (response.status === 200) {
        setMessage({ tone: "success", text: "Initial product sync queued." });
        await loadLinks();
      } else {
        setMessage({ tone: "critical", text: response.msg || "Unable to queue sync." });
      }
    } catch (error) {
      setMessage({ tone: "critical", text: "Unable to queue sync." });
    } finally {
      setSyncingId(null);
    }
  };

  const rows = links.map((link) => [
    link.source_shop?.shop_name || `Shop #${link.source_user_id}`,
    link.target_shop?.shop_name || `Shop #${link.target_user_id}`,
    `${link.payment_percentage ?? 0}%`,
    link.source_purchase_events_enabled ? (
      <Badge tone="success">Send</Badge>
    ) : (
      <Badge>Suppress</Badge>
    ),
    link.status === 1 ? <Badge tone="success">Active</Badge> : <Badge>Disabled</Badge>,
    <Badge tone={link.sync_status === 4 ? "critical" : link.sync_status === 3 ? "success" : "attention"}>
      {syncLabels[link.sync_status] || "Unknown"}
    </Badge>,
    link.last_sync_time
      ? new Date(link.last_sync_time * 1000).toLocaleString()
      : "—",
    <ButtonGroup>
      <Button onClick={() => openEdit(link)}>Edit</Button>
      <Button
        onClick={() => queueSync(link.id)}
        loading={syncingId === link.id}
        disabled={link.status !== 1}
      >
        Sync products
      </Button>
    </ButtonGroup>,
  ]);

  return (
    <Page
      title="Shop relationships"
      subtitle="Link a source shop (A) to a fulfillment shop (B)."
      primaryAction={{ content: "Create relationship", onAction: openCreate }}
    >
      <Layout>
        {message && (
          <Layout.Section>
            <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
              {message.text}
            </Banner>
          </Layout.Section>
        )}
        <Layout.Section>
          <Card>
            <DataTable
              columnContentTypes={["text", "text", "numeric", "text", "text", "text", "text", "text"]}
              headings={["Source shop (A)", "Target shop (B)", "B order traffic", "A purchase events", "Status", "Product sync", "Last sync", "Actions"]}
              rows={rows}
              loading={loading}
            />
          </Card>
        </Layout.Section>
      </Layout>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? "Edit shop relationship" : "Create shop relationship"}>
        <BlockStack gap="400">
          <Select
            label="Source shop (A)"
            options={availableShopOptions.filter(
              (option) => !option.value || option.value !== targetId,
            )}
            value={sourceId}
            onChange={setSourceId}
          />
          <Select
            label="Target shop (B)"
            options={availableShopOptions.filter(
              (option) => !option.value || option.value !== sourceId,
            )}
            value={targetId}
            onChange={setTargetId}
          />
          <Select
            label="Status"
            options={[
              { label: "Active", value: "1" },
              { label: "Disabled", value: "0" },
            ]}
            value={status}
            onChange={setStatus}
          />
          <TextField
            label="Route orders to B (%)"
            type="number"
            min={0}
            max={100}
            value={paymentPercentage}
            onChange={setPaymentPercentage}
            autoComplete="off"
            helpText="This percentage of new A-shop orders will use B for payment and fulfillment. COD orders are also created in B when B has an available COD gateway."
          />
          <Checkbox
            label="Send A-shop purchase events when B fulfills the order"
            helpText="When disabled, B-fulfilled orders do not send Purchase or COD conversion events through A-shop Facebook and TikTok pixels. A-fulfilled orders are unaffected."
            checked={sourcePurchaseEventsEnabled}
            onChange={setSourcePurchaseEventsEnabled}
          />
          {sourceId && targetId && sourceId === targetId && (
            <Banner tone="critical">Source and target shops must be different.</Banner>
          )}
          <InlineStack align="end">
            <ButtonGroup>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={saveLink}
                loading={saving}
                disabled={
                  !sourceId ||
                  !targetId ||
                  sourceId === targetId ||
                  Number(paymentPercentage) < 0 ||
                  Number(paymentPercentage) > 100
                }
              >
                Save
              </Button>
            </ButtonGroup>
          </InlineStack>
        </BlockStack>
      </Modal>
    </Page>
  );
}
